import React, { Component } from 'react'
import PropTypes from 'prop-types'

import { Accordion, Alert, Button, ProgressBar, Ratings as StarRating, Select } from '@sainsburys-tech/bolt'

import { pushEventAndTrack } from '../../helpers/taggingSerializer'
import ReviewsSummary from '../ReviewsSummary/ReviewsSummary'
import ReviewItem from '../ReviewItem/ReviewItem'
import { runBVReviewsTrack } from '../../helpers/bazaarVoiceTracking'
import ReviewsSkeleton from './Reviews.Skeleton'
import { scrollToAccordion } from '../../helpers/openAccordion'
import { AskAQuestion } from '../QandA/QandAComponents'

import * as Styled from './Reviews.styles'

const sortOptions = {
  'SubmissionTime:Desc': 'Newest',
  'SubmissionTime:Asc': 'Oldest',
  'Helpfulness:Desc': 'Most Helpful',
  'Rating:Desc': 'Highest Rating',
  'Rating:Asc': 'Lowest Rating'
}

export class Reviews extends Component {
  constructor(props) {
    super(props)

    this.state = {
      showLoadImages: false,
      sortOrder: 'SubmissionTime:Desc',
      reviewOffset: 0,
      fetchingReviews: false, // Used to show loading feedback on button
      starFilters: [false, false, false, false, false], // Which filter pills are active [5⭐️, 4⭐️, 3⭐️, 2⭐️, 1⭐️] (all false = no filtering)
      starFiltersAnalytics: [],
      isLoading: false, // Used for skeleton loader
      isMobile: false
    }
  }

  componentDidMount() {
    this.updateIsMobile()
    window.addEventListener('resize', this.updateIsMobile)
    runBVReviewsTrack('Passive', this.props)
    this.setState({ isLoading: false })
  }

  componentDidUpdate(prevProps) {
    const { fetchingReviews, isLoading } = this.state

    if (prevProps.allFetchedReviews !== this.props.allFetchedReviews && isLoading) {
      this.setState({ isLoading: false })
    }

    if (prevProps.allFetchedReviews !== this.props.allFetchedReviews && fetchingReviews) {
      this.setState({ fetchingReviews: false })
    }

    if (prevProps.partNumber !== this.props.partNumber) {
      this.setState({
        reviewOffset: 0,
        fetchingReviews: false
      })
    }
  }

  componentWillUnmount() {
    window.removeEventListener('resize', this.updateIsMobile)
  }

  updateIsMobile = () => {
    const isMobile = window.matchMedia('only screen and (max-width: 768px)').matches
    this.setState({ isMobile })
  }

  renderErrorMessage = () => {
    const defaultMessage = 'There was an error getting these reviews right now. '
    const clickMessage = 'Try again'

    return (
      <Alert kind='info' isCloseable={false} data-test='reviews-modal-alert'>
        {defaultMessage}
        <a onClick={this.errorMessageOnClick} onKeyPress={this.errorMessageOnClick} role='link' tabIndex='0'>
          {clickMessage}
        </a>
      </Alert>
    )
  }

  errorMessageOnClick = () => {
    const { partNumber, requestBVReviewData } = this.props
    const { reviewOffset, sortOrder } = this.state

    requestBVReviewData({
      partNumber,
      sortOrder,
      query: {
        Limit: 10,
        Offset: reviewOffset,
        Sort: sortOrder
      }
    })
  }

  renderReviewHeading = (overallRating, recommendationPercentage, brand) => {
    const showRecommendationMessage = brand !== 'Tu' && !!recommendationPercentage
    const ratingMessage = (
      <Styled.BVTrustmarkContainer>
        <Styled.TrustmarkMessage>
          {overallRating.toFixed(1)}
          {showRecommendationMessage && (
            <Styled.RecomendationText>
              | Recommended by {recommendationPercentage}% of customers
            </Styled.RecomendationText>
          )}
        </Styled.TrustmarkMessage>
        <Styled.BVLogo
          src='//display.ugc.bazaarvoice.com/common/images/trustmark_icons/trustmark_en.png'
          alt='BazaarVoice TrustMark'
          responsive
          lazy
        />
      </Styled.BVTrustmarkContainer>
    )

    return (
      <Styled.ProductReviewsHeader>
        Reviews
        <Styled.ProductReviewsPreview>
          <StarRating rating={overallRating} message={ratingMessage} data-test='reviews-heading-ratings' />
        </Styled.ProductReviewsPreview>
      </Styled.ProductReviewsHeader>
    )
  }

  renderWriteReview = () => {
    const url = `/product/${this.props.partNumber}/reviews`
    const renderWriteReviewButton = this.props.REVIEWS_WRITE_REVIEW_ENABLED
    return (
      renderWriteReviewButton && (
        <a href={url} data-test='reviews-write-review-link'>
          <Styled.WriteReviewButton kind='secondary'>Write a review</Styled.WriteReviewButton>
        </a>
      )
    )
  }

  renderReviewContent = (
    stats,
    optionList,
    allFetchedReviews,
    partNumber,
    showLoadImages,
    ratingSummary,
    reviewCount,
    hasError
  ) => {
    const progressBarFillPercentage = allFetchedReviews ? Math.floor((allFetchedReviews.length / reviewCount) * 100) : 0
    const { fetchingReviews, sortOrder, isLoading, starFilters, isMobile } = this.state
    const { REVIEWS } = this.props

    const renderReviewCountMessage = () => {
      if (reviewCount === 0) return null
      return (
        <div>
          We found {reviewCount} matching review{reviewCount !== 1 ? 's' : ''}:
        </div>
      )
    }

    return (
      <>
        {hasError ? (
          this.renderErrorMessage()
        ) : (
          <Styled.ReviewsSummaryContainer>
            <Styled.ReviewsSummaryItem child={1}>
              {REVIEWS && isMobile && this.renderWriteReview()}
              <ReviewsSummary
                stats={stats}
                ratingSummary={ratingSummary}
                data-test='reviews-summary'
                pillState={starFilters}
                pillSetState={this.changedStarFilters}
              />
              <AskAQuestion
                partNumber={partNumber}
                variant='reviews-container'
                dataTest='reviews-accordion-ask-question-link'
              />
              {isMobile && <Styled.Divider>{renderReviewCountMessage()}</Styled.Divider>}
            </Styled.ReviewsSummaryItem>
            <Styled.ReviewsSummaryItem child={2}>
              <Styled.SortComponentContainer>
                {!isMobile && <Styled.ReviewsText>{renderReviewCountMessage()}</Styled.ReviewsText>}
                {REVIEWS && !isMobile && this.renderWriteReview()}
                <Styled.SortComponentWrapper>
                  <Select
                    onChange={this.changeSort}
                    value={sortOrder}
                    options={optionList}
                    marginBottom={0}
                    data-test='select-change-sort'
                    aria-label='sort reviews'
                  />
                </Styled.SortComponentWrapper>
              </Styled.SortComponentContainer>
              <Styled.ReviewsContainer>
                {allFetchedReviews.map((review, key) => {
                  if (isLoading) return <ReviewsSkeleton key={key} />
                  return (
                    <React.Fragment key={key}>
                      <ReviewItem
                        review={review}
                        partNumber={partNumber}
                        key={key}
                        showLoadImages={showLoadImages}
                        data-test='review-item'
                      />
                      <Styled.ReviewDivider key={`${key}_divider`} />
                    </React.Fragment>
                  )
                })}
                {reviewCount > 10 && (
                  <Styled.MoreReviewsButtonContainer data-test='show-more-reviews-button'>
                    <Styled.MoreReviewsButtonText data-test='show-more-reviews-button-info-text'>{`You've viewed ${allFetchedReviews.length} of ${reviewCount} reviews`}</Styled.MoreReviewsButtonText>
                    <Styled.ProgressBarContainer>
                      <ProgressBar
                        value={progressBarFillPercentage}
                        scale={100}
                        height={3}
                        color='color_secondary'
                        showCount={false}
                        data-test='viewed-x-of-y-reviews-progress-bar'
                      />
                    </Styled.ProgressBarContainer>
                    {reviewCount > allFetchedReviews.length && (
                      <Button
                        kind='secondary'
                        onClick={this.showMoreReviews}
                        onKeyPress={this.showMoreReviews}
                        tabIndex='0'
                        isLoading={fetchingReviews}
                        data-test='show-x-more-reviews-button'
                      >
                        {reviewCount - allFetchedReviews.length >= 10
                          ? 'Show 10 more reviews'
                          : `Show ${reviewCount - allFetchedReviews.length} more reviews`}
                      </Button>
                    )}
                  </Styled.MoreReviewsButtonContainer>
                )}
              </Styled.ReviewsContainer>
            </Styled.ReviewsSummaryItem>
          </Styled.ReviewsSummaryContainer>
        )}
      </>
    )
  }

  changedStarFilters = activeIndex => {
    /* Just because analytics START */
    const pillValue = 5 - activeIndex
    const updatedStarFiltersAnalytics = [...this.state.starFiltersAnalytics]
    const getStarFiltersAnalyticsIndex = updatedStarFiltersAnalytics.indexOf(pillValue)
    if (getStarFiltersAnalyticsIndex === -1) {
      // If pill was not selected
      updatedStarFiltersAnalytics.unshift(pillValue)
      pushEventAndTrack({
        eventInfo: {
          eventName: 'pdp_reviewsAddRatingFilter',
          eventAction: 'PDP Reviews - Add Rating Filter'
        },
        attributes: {
          starRatingList: updatedStarFiltersAnalytics.join('|')
        }
      })
    } else {
      // remove pill
      updatedStarFiltersAnalytics.splice(getStarFiltersAnalyticsIndex, 1)
      pushEventAndTrack({
        eventInfo: {
          eventName: 'pdp_reviewsRemoveRatingFilter',
          eventAction: 'PDP Reviews - Remove Rating Filter'
        },
        attributes: {
          removedStarRating: pillValue,
          starRatingList: updatedStarFiltersAnalytics.join('|')
        }
      })
    }
    /* Just because analytics END */

    const { requestBVReviewData, partNumber } = this.props
    const { sortOrder } = this.state

    // Reset the offset on change
    const reviewOffset = 0

    this.setState(
      prevState => ({
        sortOrder,
        reviewOffset,
        starFilters: prevState.starFilters.map((currentValue, index) =>
          activeIndex === index ? !currentValue : currentValue
        ),
        starFiltersAnalytics: updatedStarFiltersAnalytics
      }),
      () => {
        requestBVReviewData({
          partNumber,
          sortOrder,
          query: {
            Limit: 10,
            Offset: reviewOffset,
            Sort: sortOrder,
            filter: this.starFiltersFormatter()
          }
        })
      }
    )
  }

  starFiltersFormatter = () => {
    let result = 'Rating:'
    if (this.state.starFilters.includes(true)) {
      this.state.starFilters.forEach((state, index) => {
        if (state) result += `${5 - index},`
      })
    } else {
      result = undefined
    }
    return result
  }

  showMoreReviews = () => {
    const { requestBVReviewData, partNumber, stats } = this.props
    const { attributes = {} } = stats
    const { reviewCount = 0 } = attributes
    const { sortOrder, reviewOffset } = this.state
    const nextReviewOffset = reviewOffset + 10

    this.setState(
      {
        reviewOffset: nextReviewOffset,
        fetchingReviews: true
      },
      () => {
        requestBVReviewData({
          partNumber,
          sortOrder,
          query: {
            Limit: 10,
            Offset: nextReviewOffset,
            Sort: sortOrder,
            filter: this.starFiltersFormatter()
          }
        })
      }
    )

    // Adobe Analytics Tracking Code
    pushEventAndTrack({
      eventInfo: {
        eventName: 'pdp_reviewsShowMore',
        eventAction: 'PDP Reviews - Show More'
      },
      attributes: {
        reviewsCount: reviewCount,
        reviewsShown: nextReviewOffset
      }
    })
    // BazaarVoice Analytics Tracking Code
    runBVReviewsTrack('FeaturedEvent', this.props, 'MoreReviews', 'pdp_moreReviews', 'Click on More Reviews')
  }
  // eslint-disable-next-line
  accordionInteraction = (index = null, show) => {
    // Index is required for the accordion, and is passed in via the onClick, however is never used and can be null
    // This causes eslint to fail here. Aim to refactor at a later date.
    this.props.onAccordionChange('review', show)
    this.setState(({ showLoadImages }) => ({
      showLoadImages: showLoadImages || show
    }))

    if (show) scrollToAccordion('#reviews', this.props.isMadeToOrder)

    // Adobe Analytics Tracking Code
    const adobeAnalytics = show ? pushEventAndTrack : () => {}
    adobeAnalytics({
      eventInfo: {
        eventName: 'pdp_showMore',
        eventAction: 'Reviews'
      }
    })

    // BazaarVoice Analytics Tracking Code
    runBVReviewsTrack(
      'FeaturedEvent',
      this.props,
      'ReadReviews',
      'pdp_showMoreShowLess',
      show ? 'Show More' : 'Show Less'
    )
  }

  changeSort = (event = { target: this.state.sortOrder }) => {
    const { requestBVReviewData, partNumber } = this.props
    const { value: sortOrder } = event.target
    // Reset the offset on sort change
    const reviewOffset = 0

    this.setState({ sortOrder, reviewOffset, isLoading: true }, () => {
      requestBVReviewData({
        partNumber,
        sortOrder,
        query: {
          Limit: 10,
          Offset: reviewOffset,
          Sort: sortOrder,
          filter: this.starFiltersFormatter()
        }
      })
    })

    runBVReviewsTrack('FeaturedEvent', this.props, 'sort', 'pdp_reviews', `sort:${sortOrder}`)
    pushEventAndTrack({
      eventInfo: {
        eventName: 'pdp_reviewsSort',
        eventAction: 'PDP Reviews - Sort'
      },
      attributes: {
        sortBy: sortOrder
      }
    })
  }

  render() {
    const { stats, partNumber, ratingSummary, allFetchedReviews = [], hasError } = this.props
    const { showLoadImages } = this.state
    const { attributes = {} } = stats
    const { overallRating = 0, recommendationPercentage = 0, reviewCount = 0 } = attributes
    const optionList = Object.keys(sortOptions).map(option => ({ title: sortOptions[option], value: option }))
    const accordionTitle =
      reviewCount > 0
        ? this.renderReviewHeading(overallRating, recommendationPercentage, this.props.attributes?.brand)
        : 'Reviews'
    return (
      <Accordion.Item
        id='reviews'
        data-test='reviews-accordion'
        name='reviews-accordion'
        index='reviews-accordion'
        as='h3'
        hasBorderBottom
        noAnimation
        title={accordionTitle}
        onClick={this.accordionInteraction}
      >
        {reviewCount < 1 ? (
          <Styled.NoReviewWrapper partNumber={partNumber} data-test='reviews-no-reviews-wrapper'>
            <Styled.NoReviewContent>There are no reviews for this product.</Styled.NoReviewContent>
            {this.renderWriteReview()}
          </Styled.NoReviewWrapper>
        ) : (
          this.renderReviewContent(
            stats,
            optionList,
            allFetchedReviews,
            partNumber,
            showLoadImages,
            ratingSummary,
            reviewCount,
            hasError
          )
        )}
      </Accordion.Item>
    )
  }
}

Reviews.propTypes = {
  // reviews: PropTypes.array.isRequired,
  partNumber: PropTypes.string,
  hasError: PropTypes.bool,
  onAccordionChange: PropTypes.func.isRequired,
  attributes: PropTypes.object.isRequired,
  stats: PropTypes.object
}

Reviews.defaultProps = {
  hasError: false,
  stats: {
    attributes: {
      overallRating: 0,
      reviewCount: 0
    }
  }
}
export default Reviews
